import React, { useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  LinearProgress,
  Stack
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { useMutation } from "@tanstack/react-query";
import {
  AddServiceResponse,
  DeleteServiceResponse,
  ServiceCategory,
  ServiceFormData,
  UpdateServiceResponse
} from "../../Graphql/types/serviceCategory";
import { fetchGraphQL } from "../../Graphql/utils";
import {
  AddServiceCategoryQuery,
  DeleteServiceCategoryQuery,
  UpdateServiceCategoryQuery
} from "../../Graphql/querries/serviceCategoryQuery";
import AlertBannerContext from "../../Contexts/alertBannerContext";

interface Props {
  open: boolean;
  onClose: () => void;
  service: ServiceCategory;
}

// Validation schema
const validationSchema: Yup.ObjectSchema<ServiceFormData> = Yup.object().shape({
  title: Yup.string()
    .required("Title is required")
    .min(3, "Title must be at least 3 characters")
    .matches(/^[A-Za-z\s'-.]+$/, "Special chars are not allowed"),
  description: Yup.string()
    .required("Description is required")
    .min(10, "Description must be at least 10 characters")
    .matches(/^[A-Za-z\s'-.()]+$/, "Special chars are not allowed")
});

const ServiceDetailDialog: React.FC<Props> = ({ open, onClose, service }) => {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<ServiceFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: service
  });

  const { setAlert } = useContext(AlertBannerContext);
  const { mutate: addService, isPending: addingService } = useMutation<
    AddServiceResponse,
    Error,
    ServiceCategory
  >({
    mutationKey: ["addTechnology"],
    mutationFn: (input) =>
      fetchGraphQL(AddServiceCategoryQuery, { input }, setAlert),
    onSuccess: () => {
      onClose();
    }
  });

  const { mutate: updateService, isPending: updatingService } = useMutation<
    UpdateServiceResponse,
    Error,
    ServiceCategory
  >({
    mutationKey: ["updateService"],
    mutationFn: (input) =>
      fetchGraphQL(UpdateServiceCategoryQuery, { input }, setAlert),
    onSuccess: () => {
      onClose();
    }
  });

  const { mutate: deleteService, isPending: deletingService } = useMutation<
    DeleteServiceResponse,
    Error,
    string
  >({
    mutationKey: ["updateService"],
    mutationFn: (id) =>
      fetchGraphQL(DeleteServiceCategoryQuery, { id }, setAlert),
    onSuccess: () => {
      onClose();
    }
  });

  const isEmpty = Object.keys(service).length === 0;

  const handleDeleteService = (): void => {
    deleteService(service.id);
  };

  const handleService = (item: ServiceFormData) => {
    const serviceItem: ServiceCategory = {
      ...service,
      ...item
    };
    if (isEmpty) {
      addService(serviceItem);
    } else {
      updateService(serviceItem);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle textAlign="center">{`${isEmpty ? "Create" : "View/edit"} Service`}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="dense"
          label="Title"
          {...register("title")}
          error={!!errors.title}
          helperText={errors.title?.message}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Description"
          multiline
          minRows={4}
          {...register("description")}
          error={!!errors.description}
          helperText={errors.description?.message}
        />
      </DialogContent>
      {(addingService || updatingService || deletingService) && (
        <Stack width="100%" paddingBottom="1em" alignSelf="center">
          <LinearProgress />
        </Stack>
      )}
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit(handleService)} color="primary">
          {`${isEmpty ? "Create" : "Save updates"}`}
        </Button>
        <Button
          onClick={handleDeleteService}
          startIcon={<DeleteForeverIcon />}
          sx={{ color: "red" }}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceDetailDialog;
